import React from 'react';
import PropTypes from 'prop-types';
import useRefreshIndicator from 'Views/Onboarding/Pages/Survey/SurveyContentBody/RefreshAccountsIndicator/useRefreshIndicator';

const RefreshAccountsContext = React.createContext(undefined);

// Actions
export const REFRESH_STARTED = 'refresh-started';
export const REFRESH_SUCCEEDED = 'refresh-succeeded';
export const REFRESH_FAILED = 'refresh-failed';

const init = () => {
  return {
    isRefreshing: false,
    refreshError: null,
  };
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case REFRESH_STARTED: {
      return {
        ...state,
        isRefreshing: true,
        refreshError: null,
      };
    }
    case REFRESH_SUCCEEDED: {
      return {
        ...state,
        isRefreshing: false,
      };
    }
    case REFRESH_FAILED: {
      return {
        ...state,
        isRefreshing: false,
        refreshError: action.refreshError,
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
};

const RefreshAccountsProvider = ({
  children,
}) => {
  const [state, dispatch] = React.useReducer(reducer, null, init);
  const {
    status,
    progress,
    handleStartWorking,
    handleReset,
    handleFinishWorking,
  } = useRefreshIndicator({
    duration: 60000,
    finishDuration: 1000,
  });

  const handleRefreshStarted = React.useCallback(async () => {
    handleStartWorking();
    dispatch({ type: REFRESH_STARTED });
  }, []);

  const handleRefreshSucceeded = React.useCallback(async () => {
    handleFinishWorking();
    dispatch({ type: REFRESH_SUCCEEDED });
  }, []);

  const handleRefreshFailed = React.useCallback(async (err) => {
    handleReset();
    dispatch({ type: REFRESH_SUCCEEDED, refreshError: err });
  }, []);

  const memoizedValue = React.useMemo(() => {
    return {
      isRefreshing: state.isRefreshing,
      refreshError: state.refreshError,
      status,
      progress,
      handleRefreshStarted,
      handleRefreshSucceeded,
      handleRefreshFailed,
    };
  }, [
    state,
    status,
    progress,
    handleRefreshStarted,
    handleRefreshSucceeded,
    handleRefreshFailed,
  ]);

  return (
    <RefreshAccountsContext.Provider value={memoizedValue}>
      {children}
    </RefreshAccountsContext.Provider>
  );
};

// Use Survey State Context
const useRefreshAccountsContext = () => {
  const context = React.useContext(RefreshAccountsContext);
  if (context === undefined) {
    throw new Error('useRefreshAccountsContext must be used within a RefreshAccountsProvider');
  }
  return context;
};

RefreshAccountsProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

export {
  RefreshAccountsProvider,
  useRefreshAccountsContext,
};
